import { routes } from "../../assets/constants";
import { ApiMethod } from "../models/api-types.type";
import { buildQueryParams } from "../utils/utils";
import { useAuthContext } from "./use-auth-context.hook";

export interface LeadAttribution {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
  assetUuid?: string;
  platform?: string;
  referrer?: string;
  landingPage?: string;
  [key: string]: unknown;
}

export interface Lead {
  _id: string;
  email: string;
  /** This vendor's first/last sight of the lead (the API scopes them per vendor). */
  firstSeenAt: string;
  lastSeenAt: string;
  /** Events this vendor has seen from the lead. */
  eventCount: number;
  vendorIds?: string[];
  visitorIds: string[];
  products: LeadProduct[];
  /** Explicit opt-in to this vendor's marketing mail. Never inferred from a download. */
  marketingConsent?: boolean;
  unsubscribedAt?: string | null;
  /** What first brought them in (first touch). */
  attribution?: LeadAttribution;
  /** Results downloaded from this vendor's visualizer. */
  downloadCount?: number;
  lastDownloadAt?: string | null;
  /** Emails this vendor has sent them. */
  emailsSent?: number;
  lastEmailedAt?: string | null;
}

export interface LeadProduct {
  productId: string;
  title: string;
  thumbnailPath: string;
  link: string;
  addToCartLink?: string;
  usageCount: number;
  firstUsedAt: string;
  lastUsedAt: string;
}

export interface LeadsResponse {
  data: Lead[];
  /** Leads captured beyond the plan: counted by the API, never returned. */
  lockedCount?: number;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface LeadsQueryParams {
  page?: number;
  limit?: number;
  vendorId?: string;
  email?: string;
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export const useLeadsApi = () => {
  const { sendAuthGuardedRequest } = useAuthContext();

  const findAllLeads = async (params?: LeadsQueryParams): Promise<LeadsResponse> => {
    const queryParams = [];
    
    if (params?.page) queryParams.push({ key: "page", value: params.page.toString() });
    if (params?.limit) queryParams.push({ key: "limit", value: params.limit.toString() });
    if (params?.vendorId) queryParams.push({ key: "vendorId", value: params.vendorId });
    if (params?.email) queryParams.push({ key: "email", value: params.email });
    if (params?.startDate) queryParams.push({ key: "startDate", value: params.startDate });
    if (params?.endDate) queryParams.push({ key: "endDate", value: params.endDate });
    if (params?.sortBy) queryParams.push({ key: "sortBy", value: params.sortBy });
    if (params?.sortOrder) queryParams.push({ key: "sortOrder", value: params.sortOrder });

    const queryString = buildQueryParams(queryParams);

    const response = await sendAuthGuardedRequest(
      ApiMethod.GET,
      routes.leads.findAll + queryString,
    ) as LeadsResponse;
    
    return response;
  };

  const findLeadByEmail = async (email: string): Promise<Lead | null> => {
    const response = await findAllLeads({ email, limit: 1 });
    return response.data.length > 0 ? response.data[0] : null;
  };

  const getAllLeadsEmails = async (): Promise<string[]> => {
    // Fetch all leads with minimal data (just emails)
    const response = await findAllLeads();
    return response.data.map(lead => lead.email);
  };

  const extractLeads = async (): Promise<void> => {
    await sendAuthGuardedRequest(
      ApiMethod.POST,
      routes.leads.extract,
    );
  };

  return {
    findAllLeads,
    findLeadByEmail,
    getAllLeadsEmails,
    extractLeads
  };
}; 